import { Fragment, useCallback, useEffect, useState } from "react";
import PageLayout from "../Layouts/PageLayout";
import Spinner from "../FieldComponets/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { PAGES } from "../../Constants/CommonConstants";
import { MaterialReactTable } from "material-react-table";
import { paginationQuery } from "../../utils/GlobalHelper";
import type { PaginationState } from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";
import { SubscriptionPaymentListAction } from "../../Redux/Action/SubscriptionPaymentAction";
import { url } from "../../Config/Config";
import { encodeID, getDate } from "../../utils/Helper";
import { FormattedMessage, useIntl } from "react-intl";

function statusBadge(row: any) {
  const s = row?.status;
  if (s === 0) {
    return <span className="badge bg-warning text-dark"><FormattedMessage id="pending" /></span>;
  }
  if (s === 1) {
    return <span className="badge bg-success"><FormattedMessage id="approved" /></span>;
  }
  if (s === 2) {
    return <span className="badge bg-danger"><FormattedMessage id="rejected" /></span>;
  }
  return <span className="badge bg-secondary">—</span>;
}

function SubscriptionTransactionHistoryList() {
  const dispatch = useDispatch<any>();
  const intl = useIntl();
  const listState = useSelector((state: any) => state.SubscriptionPaymentListState);

  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PAGES?.pageIndex,
    pageSize: PAGES?.pageSize,
  });
  const [sorting] = useState<any>(["DESC"]);
  const [rowCount, setRowCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState<string>("");

  const fetchList = useCallback(() => {
    const query: any = paginationQuery(
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      "",
      [],
    );
    query.status_filter =
      statusFilter === "all" ? "all" : statusFilter;
    if (planFilter) {
      query.plan_code_filter = planFilter;
    }
    dispatch(SubscriptionPaymentListAction(query));
  }, [
    dispatch,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    statusFilter,
    planFilter,
  ]);

  useEffect(() => {
    setLoading(true);
    fetchList();
    setLoading(false);
  }, [fetchList]);

  useEffect(() => {
    setData(listState?.data?.data?.rows);
    setRowCount(listState?.data?.data?.count);
  }, [listState?.data]);

  const columns: any = [
    {
      accessorKey: "S.NO",
      header: intl.formatMessage({ id: "s_no" }),
      accessorFn: (_row: object, index: number) =>
        pagination.pageIndex * pagination.pageSize + index + 1,
      size: 50,
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "salon_name" }),
      accessorFn: (row: any) => {
        const name = row?.salon?.salon_name || "—";
        const sid = row?.salon?.id;
        if (!sid || name === "—") return name;
        return (
          <Link to={`${url}/salon-details/${encodeID(sid)}`} className="fw-semibold text-decoration-none">
            {name}
          </Link>
        );
      },
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "salon_email" }),
      accessorFn: (row: any) => row?.salon?.email || "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "subscription_plan_type" }),
      accessorFn: (row: any) => row?.subscription_plan_type || "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "subscription_purchase_date" }),
      accessorFn: (row: any) =>
        row?.subscription_purchase_date
          ? getDate(row.subscription_purchase_date)
          : "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "subscription_active_date" }),
      accessorFn: (row: any) =>
        row?.subscription_active_date
          ? getDate(row.subscription_active_date)
          : "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "subscription_suspended" }),
      accessorFn: (row: any) =>
        row?.subscription_suspended_at
          ? getDate(row.subscription_suspended_at)
          : "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "subscription_status" }),
      accessorFn: (row: any) => row?.subscription_status_label || "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "requested_plan" }),
      accessorFn: (row: any) => row?.plan?.name || "—",
      enableSorting: false,
    },
    {
      accessorKey: "transaction_id",
      header: intl.formatMessage({ id: "transaction_id" }),
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "status" }),
      accessorFn: (row: any) => statusBadge(row),
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "submitted" }),
      accessorFn: (row: any) =>
        row?.created_at ? new Date(row.created_at).toLocaleString() : "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "reviewed" }),
      accessorFn: (row: any) =>
        row?.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : "—",
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "remark" }),
      accessorFn: (row: any) => (row?.admin_remark ? String(row.admin_remark) : "—"),
      enableSorting: false,
    },
    {
      header: intl.formatMessage({ id: "proof" }),
      accessorFn: (row: any) =>
        row.payment_proof_url ? (
          <a href={row.payment_proof_url} target="_blank" rel="noreferrer">
            {intl.formatMessage({ id: "view" })}
          </a>
        ) : (
          "—"
        ),
      enableSorting: false,
    },
  ];

  return (
    <Fragment>
      {(loading || listState?.loading) ? <Spinner /> : ""}
      <PageLayout
        title={intl.formatMessage({ id: "subscription_transaction_history" })}
        breadcrumbs={{
          secLast: {
            link: "/subscription-transaction-history",
            name: intl.formatMessage({ id: "subscription_history" }),
          },
        }}
        buttons={[
          {
            url: "/subscription-payment-requests",
            className: "btn btn-outline-primary",
            text: intl.formatMessage({ id: "pending_approvals" }),
            show: true,
          },
          {
            url: "/offline-bank-settings",
            className: "btn btn-primary",
            text: intl.formatMessage({ id: "bank_settings" }),
            show: true,
          },
        ]}
      >
        <div className="row g-3 align-items-end mb-3">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label className="form-label small text-muted mb-1" htmlFor="sub-hist-status-filter">
              <FormattedMessage id="payment_status" />
            </label>
            <select
              id="sub-hist-status-filter"
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <option value="all">{intl.formatMessage({ id: "approved_and_rejected" })}</option>
              <option value="1">{intl.formatMessage({ id: "approved_only" })}</option>
              <option value="2">{intl.formatMessage({ id: "rejected_only" })}</option>
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label className="form-label small text-muted mb-1" htmlFor="sub-hist-plan-filter">
              <FormattedMessage id="requested_plan" />
            </label>
            <select
              id="sub-hist-plan-filter"
              className="form-select form-select-sm"
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <option value="">{intl.formatMessage({ id: "all_plans" })}</option>
              <option value="basic">{intl.formatMessage({ id: "basic_plan" })}</option>
              <option value="premium">{intl.formatMessage({ id: "premium_plan" })}</option>
            </select>
          </div>
        </div>
        {data && (
          <MaterialReactTable
            columns={columns}
            data={data}
            enableGlobalFilter={false}
            enableColumnFilters={false}
            manualPagination
            rowCount={rowCount}
            onPaginationChange={setPagination}
            state={{ pagination }}
          />
        )}
      </PageLayout>
    </Fragment>
  );
}

export default SubscriptionTransactionHistoryList;
